import omit from 'lodash/omit'
import { computed, ref } from 'vue'

import { useTeamProperties } from '@/modules/Classes/useTeamProperties'
import { useToast } from '@/uiKit/Toast/useToast'
import { useTeamStore } from '@/pinia/useTeamStore'
import { setContext } from '@/services/sentry'
import type {
  PropertyValue,
  TeamItemProperty,
  TeamProperty,
  TeamPropertyValue,
} from '@/store/types/PropertyTypes'
import { TeamPropertyGranularity } from '@/store/types/PropertyTypes'
import type {
  DatasetItemPropertiesResponse,
  TeamPropertyRequestBody,
} from '@/backend/darwin/teamProperties'
import {
  createTeamProperty,
  deleteTeamProperty,
  deleteTeamPropertyValue,
  getItemTeamItemProperties,
  reorderTeamPropertyValues,
  setItemTeamPropertyValue,
  type TeamPropertyUpdateRequestBody,
  updateTeamProperty,
  updateTeamPropertyValue,
} from '@/backend/darwin/teamProperties'
import { getErrorMessage } from '@/backend/error'
import type { ParsedError } from '@/backend/error/types'
import type { V2WorkflowCommandResponse } from '@/store/types/V2WorkflowCommandResponse'
import type { ApiResponse } from '@/store/types'
import { useWorkflowInWorkviewStore } from '@/modules/Workview/useWorkflowInWorkviewStore'
import { defineComposable } from '@/core/utils/defineComposable'
import type { V2WorkflowStageInstancePayload } from '@/store/types/V2WorkflowStageInstancePayload'
import type { V2WorkflowStagePayload } from '@/store/types/V2WorkflowStagePayload'
import { useAuth } from '@/modules/Auth/useAuth'

export const CREATE_PROPERTY_SUCCESS_MESSAGE = 'Property created successfully'
export const UPDATE_PROPERTY_SUCCESS_MESSAGE = 'Property updated successfully'
export const DELETE_PROPERTY_SUCCESS_MESSAGE = 'Property deleted successfully'

class UpdateError extends Error {}

/** Store used to create, update or delete individual properties */
export const useTeamProperty = defineComposable(() => {
  const teamStore = useTeamStore()
  const toast = useToast()
  const teamProperties = useTeamProperties()
  const workflowStore = useWorkflowInWorkviewStore()
  const auth = useAuth()

  const teamSlug = computed(() => teamStore.currentTeam?.slug)

  const defaultPropertyGranularity = ref(TeamPropertyGranularity.ANNOTATION)

  const createProperty = async (
    property: TeamPropertyRequestBody,
  ): Promise<Awaited<ReturnType<typeof createTeamProperty>> | void> => {
    if (!teamSlug.value) {
      return
    }

    const response = await createTeamProperty(teamSlug.value, property)
    if ('error' in response) {
      const message = getErrorMessage(response.error).join(', ')
      toast.error({ meta: { title: message } })
    } else {
      // Add created property to the list of properties
      teamProperties.setProperties([...teamProperties.properties, response.data])
      toast.success({ meta: { title: CREATE_PROPERTY_SUCCESS_MESSAGE } })
    }

    return response
  }

  const getRequestBody = (
    property: Omit<TeamProperty, 'team_id' | 'slug'>,
  ): TeamPropertyRequestBody => ({
    name: property.name,
    type: property.type,
    description: property.description,
    granularity: property.granularity,
    dataset_ids: property.dataset_ids,
    property_values: property.property_values?.map((v) => omit(v, 'id', 'position')) || [],
    annotation_class_id: property.annotation_class_id,
    required: property.required,
  })
  /**
   * Return the main body to update.
   *
   * Since main body is not able to update the property value we need to remove
   * already created values from it and send them separately with a value update request.
   */
  const getBodyToUpdate = (
    property: Omit<TeamProperty, 'team_id' | 'slug'>,
  ): TeamPropertyRequestBody => {
    const currentPropIndex = teamProperties.properties.findIndex((p) => p.id === property.id)

    if (currentPropIndex < 0) {
      return getRequestBody(property)
    }

    const currentProp = teamProperties.properties[currentPropIndex]

    currentProp.property_values.forEach((currentProp) => {
      // Get property that already created
      const propertyToUpdate = property.property_values.findIndex((p) => p.id === currentProp.id)
      if (propertyToUpdate < 0) {
        return
      }
      // Remove from the main body request (it supports only property creation)
      property.property_values.splice(propertyToUpdate, 1)
    })

    return getRequestBody(property)
  }

  const updatePropertyValuesOrder = async (
    propertyId: TeamProperty['id'],
    orderedIds: TeamPropertyValue['id'][],
  ): Promise<void> => {
    if (!teamSlug.value) {
      return
    }
    const response = await reorderTeamPropertyValues(teamSlug.value, propertyId, orderedIds)
    if ('error' in response) {
      const message =
        typeof response.error.message === 'string' ? response.error.message : 'Something went wrong'

      throw new UpdateError(message)
    }
  }

  const updatePropertyValue = async (
    propertyId: TeamItemProperty['id'],
    propertyValue: PropertyValue,
  ): Promise<Awaited<ReturnType<typeof updateTeamPropertyValue>> | void> => {
    if (!teamSlug.value) {
      return
    }

    const response = await updateTeamPropertyValue(teamSlug.value, propertyId, propertyValue.id, {
      color: propertyValue.color,
      value: propertyValue.name,
    })
    if ('error' in response) {
      const message =
        typeof response.error.message === 'string' ? response.error.message : 'Something went wrong'

      throw new UpdateError(message)
    }
  }

  /**
   * NOTE: updateProperty will be updated as soon as API implements the endpoint
   * for batch property values update
   */
  const updateProperty = async (
    property: Omit<TeamProperty, 'team_id' | 'slug'>,
  ): Promise<Awaited<ReturnType<typeof updateTeamProperty>> | void> => {
    if (!teamSlug.value) {
      return
    }
    const teamSlugValue = teamSlug.value

    const payloadBody = getBodyToUpdate(property)
    const sanitisedPayloadBody: TeamPropertyUpdateRequestBody = omit(payloadBody, [
      'annotation_class_id',
      'granularity',
    ])

    try {
      // Main property body update
      const propertyUpdateResponse = await updateTeamProperty(
        teamSlugValue,
        property.id,
        sanitisedPayloadBody,
      )

      if ('error' in propertyUpdateResponse) {
        const message =
          typeof propertyUpdateResponse.error.message === 'string'
            ? propertyUpdateResponse.error.message
            : 'Something went wrong'

        throw new UpdateError(message)
      }

      if (propertyUpdateResponse && 'data' in propertyUpdateResponse) {
        // Replace updated property in the list of properties
        teamProperties.setProperties(
          teamProperties.properties.map((p) =>
            p.id === propertyUpdateResponse.data.id ? propertyUpdateResponse.data : p,
          ),
        )
      }

      const storeProperty = teamProperties.properties.find(
        (p) => p.id === propertyUpdateResponse.data.id,
      )
      if (storeProperty) {
        storeProperty.property_values = storeProperty.property_values.map((p) => {
          const propertyValue = propertyUpdateResponse.data.property_values.find(
            (pv) => pv.id === p.id,
          )
          return propertyValue || p
        })
      }

      toast.success({ meta: { title: UPDATE_PROPERTY_SUCCESS_MESSAGE } })

      return propertyUpdateResponse
    } catch (e: unknown) {
      if (e instanceof UpdateError) {
        toast.error({ meta: { title: e.message } })
        return
      }

      setContext('updateProperty error', { error: e })
      return
    }
  }

  const deleteProperty = async (
    id: string,
  ): Promise<Awaited<ReturnType<typeof deleteTeamProperty>> | void> => {
    if (!teamSlug.value) {
      return
    }

    const response = await deleteTeamProperty(teamSlug.value, id)

    if ('error' in response) {
      const message =
        typeof response.error.message === 'string' ? response.error.message : 'Something went wrong'

      toast.error({ meta: { title: message } })
    } else {
      // Remove deleted property from the list of properties
      teamProperties.setProperties(teamProperties.properties.filter((p) => p.id !== id))
      toast.success({ meta: { title: DELETE_PROPERTY_SUCCESS_MESSAGE } })
    }
  }

  const deletePropertyValue = async (
    propertyId: string,
    propertyValueId: string,
  ): Promise<void> => {
    if (!teamSlug.value) {
      return
    }

    const response = await deleteTeamPropertyValue(teamSlug.value, propertyId, propertyValueId)

    if ('error' in response) {
      const message =
        typeof response.error.message === 'string' ? response.error.message : 'Something went wrong'

      toast.error({ meta: { title: message } })

      throw new Error(message)
    }

    toast.success({ meta: { title: DELETE_PROPERTY_SUCCESS_MESSAGE } })
  }

  const getItemPropertiesForItem = async (
    itemId: string,
    assignedStageInstance?: V2WorkflowStageInstancePayload,
  ): Promise<DatasetItemPropertiesResponse | undefined> => {
    if (!teamSlug.value) {
      return
    }

    const config = assignedStageInstance?.stage?.config

    // Check if this is a parallel blind stage (can have parallel_stages or parallel_stage_ids)
    const isParallelBlindStage =
      config && ('parallel_stages' in config || 'parallel_stage_ids' in config)

    // For parallel blind stages, check if user has admin privileges and can see all annotation groups
    if (isParallelBlindStage && auth.isAuthorized('view_full_datasets')) {
      // Get all annotation group IDs from the parallel stages
      let allAnnotationGroupIds: string[] = []

      if ('parallel_stages' in config) {
        // New format: array of objects with stage_id
        const parallelStagesConfig = config as { parallel_stages: { stage_id: string }[] }
        const parallelStageIds = parallelStagesConfig.parallel_stages.map((stage) => stage.stage_id)

        // Look up the actual stage objects and extract annotation_group_id from their configs
        allAnnotationGroupIds = parallelStageIds
          .map((stageId) => {
            const stage = workflowStore.workflowStages.find(
              (s: V2WorkflowStagePayload) => s.id === stageId,
            )
            // Check if stage has config with annotation_group_id (annotate/review stages)
            if (stage?.config && 'annotation_group_id' in stage.config) {
              return stage.config.annotation_group_id
            }
            return undefined
          })
          .filter((id): id is string => id !== undefined)
      } else if ('parallel_stage_ids' in config) {
        // Legacy format: array of strings
        const parallelStageIdsConfig = config as { parallel_stage_ids: string[] }
        const parallelStageIds = parallelStageIdsConfig.parallel_stage_ids

        // Look up the actual stage objects and extract annotation_group_id from their configs
        allAnnotationGroupIds = parallelStageIds
          .map((stageId) => {
            const stage = workflowStore.workflowStages.find(
              (s: V2WorkflowStagePayload) => s.id === stageId,
            )
            // Check if stage has config with annotation_group_id (annotate/review stages)
            if (stage?.config && 'annotation_group_id' in stage.config) {
              return stage.config.annotation_group_id
            }
            return undefined
          })
          .filter((id): id is string => id !== undefined)
      }

      const response = await getItemTeamItemProperties(
        teamSlug.value,
        itemId,
        allAnnotationGroupIds,
      )
      return response.data
    }

    // For regular annotate stages or regular users in parallel blind stages
    const shouldFilterByAnnotationGroup =
      config && 'annotation_group_id' in config && config.annotation_group_id

    if (shouldFilterByAnnotationGroup) {
      const annotationGroupId = (config as { annotation_group_id: string }).annotation_group_id

      const response = await getItemTeamItemProperties(teamSlug.value, itemId, [annotationGroupId])
      return response.data
    }

    const response = await getItemTeamItemProperties(teamSlug.value, itemId)
    return response.data
  }

  const setItemPropertyValue = (
    itemId: string | undefined,
    propertyId: string,
    propertyValueIds: string[] | [{ text: string }],
  ): Promise<ParsedError | ApiResponse<V2WorkflowCommandResponse>> | undefined => {
    if (!teamSlug.value) {
      return
    }

    if (!itemId) {
      console.warn('setItemPropertyValue called with undefined itemId')
      return
    }

    if (!workflowStore.selectedStageInstance?.id || !workflowStore.selectedStage?.id) {
      return
    }

    // For parallel blind stages, use the assigned stage instance instead of selected stage instance
    // This ensures we use the correct inner stage instance, not the parent PBS stage
    const stageInstance = workflowStore.assignedStageInstance || workflowStore.selectedStageInstance
    let annotationGroupId: string | null = null

    if (stageInstance?.stage?.config) {
      const config = stageInstance.stage.config
      if ('annotation_group_id' in config) {
        annotationGroupId = config.annotation_group_id || null
      }
    }

    // Use the actual stage instance for the command (assigned stage for PBS, selected stage otherwise)
    const stageInstanceId = stageInstance?.id || workflowStore.selectedStageInstance.id
    const stageId = stageInstance?.stage?.id || workflowStore.selectedStage.id

    // Optimistic UI: updates the current item team property value
    teamProperties.setCurrentItemPropertyValues(propertyId, propertyValueIds)
    return setItemTeamPropertyValue(
      teamSlug.value,
      itemId,
      propertyId,
      propertyValueIds,
      stageInstanceId,
      stageId,
      annotationGroupId,
    )
  }

  const isValidPropertyResponse = (
    response: { data: TeamProperty | TeamItemProperty } | ParsedError | void,
  ): response is { data: TeamProperty | TeamItemProperty } => !!response && !('error' in response)

  return {
    teamSlug,
    isValidPropertyResponse,
    createProperty,
    updateProperty,
    deleteProperty,
    updatePropertyValue,
    updatePropertyValuesOrder,
    deletePropertyValue,
    setItemPropertyValue,
    getItemPropertiesForItem,

    defaultPropertyGranularity,
  }
})
